/* eslint-disable max-len */
import { ColumnsIcon, ListIcon, PlusIcon, SearchIcon } from 'assets/icons';
import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { Button, ButtonStyle } from 'common/button';
import { Breakpoints, NavSections } from 'common/enums';
import { InfoLabel } from 'common/info-label';
import { Input } from 'common/input';
import { Notification } from 'common/notification';
import { QrCodesDetailsModal } from 'common/qr-codes-details-modal';
import { Select } from 'common/select';
import { Spinner } from 'common/spinner';
import {
  vaultsOrderByOptions,
  vaultsTitles,
  paginationDefault,
} from 'config/constants';
import { appActions, AppContext } from 'context';
import { HelpInfoSections } from 'context/app-context/context-reducer';
import { trackCustomEvent } from 'helpers/analytics';
import {
  classnames,
  getVaultStatusLabel,
  isTemporaryUser,
} from 'helpers/utils';
import { useHandleApiError } from 'hooks/use-handle-api-error';
import { useMediaQuery } from 'hooks/use-media-query';
import { cloneDeep, debounce } from 'lodash';
import { VaultsQueryParams } from 'models/vaultsQueryParams';
import { VaultsController } from 'networking/controllers/vaults-controller';
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { goToPage, RouteName } from 'routes';
import styles from './vaults.module.scss';
import { VaultCardLarge } from './components/vault-card-large';
import { VaultCardMobile } from './components/vault-card-mobile';
import { VaultCardRow } from './components/vault-card-row';
import { VaultsFilters } from './components/vaults-filters';

const ROWS_PER_PAGE_DEFAULT = paginationDefault.pageSize;
const INITIAL_CURRENT_PAGE = paginationDefault.pageNum;
const DEFAULT_SORT_BY_OBJECT = vaultsOrderByOptions.find(
  (elem: SelectType) => elem.id === paginationDefault.lastUpdate.id,
);

const Vaults: React.FC = () => {
  const { state, dispatch } = useContext(AppContext);

  const [vaults, setVaults] = useState<VaultType[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<PaginationType>();
  const [isLayoutsColumns, setIsLayoutsColumns] = useState(true);
  const [search, setSearch] = useState('');
  const [orderByOption, setOrderByOption] = useState<SelectType>(
    DEFAULT_SORT_BY_OBJECT!,
  );
  const [fetching, setFetching] = useState(true);
  const [fetchingVaults, setFetchingVaults] = useState(false);
  const [showError, setShowError] = useState(false);
  const [queryParams, setQueryParams] = useState<VaultsQueryParams>({
    pageSize: ROWS_PER_PAGE_DEFAULT,
    pageNum: INITIAL_CURRENT_PAGE,
    sortBy: DEFAULT_SORT_BY_OBJECT!.id,
  });
  const [showQrCodesModal, setShowQrCodesModal] = useState(false);
  const [qrCodesForModal, setQrCodesForModal] = useState<QrCode[]>([]);

  const firstCardRef = useRef(null);

  const didMount = useRef(false);
  const concatNextVaults = useRef(false);
  const mobile = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);
  const setApiError = useHandleApiError(() => {
    setShowError(true);
  });

  const fetchVaults = async () => {
    try {
      const { data, pagination } =
        await VaultsController.getVaults(queryParams);

      let newVaults = data;

      if (concatNextVaults.current) {
        const actualVaults = cloneDeep(vaults);
        newVaults = actualVaults.concat(newVaults);
      }

      setVaults(newVaults);
      setPaginationInfo(pagination);
    } catch (err: any) {
      setApiError(err);
    } finally {
      setFetching(false);
      setFetchingVaults(false);
    }
  };

  useEffect(() => {
    if (!fetchingVaults) {
      if (vaults.length) {
        dispatch({
          type: appActions.SET_HELP_SECTION,
          helpSection: HelpInfoSections.vaults_filled,
        });
      } else {
        dispatch({
          type: appActions.SET_HELP_SECTION,
          helpSection: HelpInfoSections.vaults_empty,
        });
      }
    }

    return () => {
      dispatch({
        type: appActions.SET_HELP_SECTION,
      });
    };
  }, [vaults, fetchingVaults]);

  useEffect(() => {
    // if is a temporary user he can't see the vaults page
    if (isTemporaryUser(state.data.user)) {
      if (state.data.user.trialVaultId) {
        goToPage(RouteName.VaultDetail, { id: state.data.user.trialVaultId });
      } else {
        goToPage(RouteName.NotFound);
      }
    }
  }, []);

  useEffect(() => {
    dispatch({
      type: appActions.ACTUAL_SECTION,
      actualSection: NavSections.vaults,
    });

    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    fetchVaults();
  }, []);

  useEffect(() => {
    setFetchingVaults(true);
    fetchVaults();
  }, [queryParams]);

  const navigateToVaultCreation = () => {
    goToPage(RouteName.VaultNew);
    trackCustomEvent('startVaultCreation', { step: 'home' });
  };

  const handleDebounceFn = (value: string) => {
    const params = { ...queryParams };
    if (value === '') delete params.search;
    else params.search = value;

    setQueryParams({ ...params, pageNum: 1 });
  };

  const debounceFn = useCallback(debounce(handleDebounceFn, 500), [
    queryParams,
  ]);

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    concatNextVaults.current = false;
    setSearch(value);
    debounceFn(value);
  };

  const onClickSortBy = (option: SelectType) => {
    concatNextVaults.current = false;
    setOrderByOption(option);

    setQueryParams((prevState) => ({
      ...prevState,
      sortBy: option.id as string,
      pageNum: 1,
    }));
  };

  const getMoreVaults = async () => {
    const newPageNum = queryParams.pageNum + 1;
    concatNextVaults.current = true;
    setQueryParams((prevState) => ({ ...prevState, pageNum: newPageNum }));
  };

  const handleClickCard = (id: number, keyEvent?: boolean) => () => {
    if (!keyEvent) {
      goToPage(RouteName.VaultDetail, { id });
    } else if (!showQrCodesModal) {
      goToPage(RouteName.VaultDetail, { id });
    }
  };

  const handleClickQrIcon = async (id: number) => {
    setShowQrCodesModal(true);
    const qrCodes = vaults.find((vault) => vault.id === id)?.qrCodes ?? [];
    setQrCodesForModal(qrCodes);
  };

  const showVaultStatusLabel = (alb: VaultType, positionClass: string) => {
    const { withIcon, statusValue } = getVaultStatusLabel(alb);

    return (
      statusValue && (
        <InfoLabel
          className={classnames(
            'text__body__semi__bold__overline__gray700',
            styles.vaultStatusBox,
            positionClass,
          )}
          withIcon={withIcon}
          content={
            <p
              className={classnames(
                'text__body__semi__bold__overline__gray700',
              )}
            >
              {statusValue}
            </p>
          }
        />
      )
    );
  };

  const showHeaderRow = () => (
    <div className={styles.headerRow}>
      <div className={styles.headerContent}>
        <div className={styles.headerFirstRow}>
          <div
            className={classnames(
              styles.vaultTitle,
              mobile
                ? 'text__heading3__mobile__gray800'
                : 'text__heading4__gray800',
            )}
          >
            {' '}
            Click the vault you want to open{' '}
          </div>
        </div>
        <div className={styles.headerSecondRow}>
          <div className={styles.inputSearch}>
            <Input
              id="search"
              name="search"
              placeholder="Search"
              value={search}
              withValidation={false}
              withIconEnd={
                <SearchIcon className={classnames(styles.searchIcon)} />
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeSearch(e)}
            />
          </div>
          <div className={styles.sortAndFilters}>
            <div className={styles.orderByDropdown}>
              <span className="text__body__regular__small__gray700 mr-8">
                {' '}
                Sort by:{' '}
              </span>
              <Select
                options={vaultsOrderByOptions}
                optionSelected={orderByOption}
                onClickOption={(option) => onClickSortBy(option)}
                id="orderByOption"
                value={orderByOption.value}
                placeholder="Select option"
              />
            </div>
            <VaultsFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
            <div className={styles.iconsLayout}>
              <button type="button" onClick={() => setIsLayoutsColumns(true)}>
                <ColumnsIcon
                  className={classnames(
                    isLayoutsColumns
                      ? styles.iconSelected
                      : styles.iconNotSelected,
                  )}
                />
              </button>
              <button type="button" onClick={() => setIsLayoutsColumns(false)}>
                <ListIcon
                  className={classnames(
                    !isLayoutsColumns
                      ? styles.iconSelected
                      : styles.iconNotSelected,
                  )}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const showCardsLarge = () => (
    <div className={styles.cardsLarge}>
      {vaults.map((vault, index) => (
        <VaultCardLarge
          cardRef={index === 0 ? firstCardRef : undefined}
          key={vault.id}
          vault={vault}
          showVaultStatusLabel={showVaultStatusLabel}
          notifyClickCard={handleClickCard}
          notifyClickQrIcon={(id) => {
            handleClickQrIcon(id);
          }}
        />
      ))}
      <div
        className={styles.newVaultCard}
        ref={vaults.length === 0 ? firstCardRef : undefined}
      >
        <button
          type="button"
          className={styles.newVaultCardContent}
          onClick={navigateToVaultCreation}
        >
          <Button className={styles.openVaultButton}>
            <PlusIcon /> New vault
          </Button>
        </button>
      </div>
    </div>
  );

  const showCardsRow = () => (
    <div className={styles.cardsRows}>
      <div className={styles.titles}>
        {Object.keys(vaultsTitles).map((title) => (
          <div
            key={title}
            className={classnames(
              styles[title],
              'text__body__semi__bold__medium__gray800',
            )}
          >
            {vaultsTitles[title]}
          </div>
        ))}
      </div>
      {vaults.map((vault) => (
        <VaultCardRow
          key={vault.id}
          vault={vault}
          showVaultStatusLabel={showVaultStatusLabel}
          notifyClickCard={handleClickCard}
          notifyClickQrIcon={(id) => {
            handleClickQrIcon(id);
          }}
        />
      ))}
    </div>
  );

  const showCardsMobile = () => (
    <div className={styles.cardsMobile}>
      {vaults.map((vault) => (
        <VaultCardMobile
          key={vault.id}
          vault={vault}
          showVaultStatusLabel={showVaultStatusLabel}
          notifyClickCard={handleClickCard}
          notifyClickQrIcon={(id) => {
            handleClickQrIcon(id);
          }}
        />
      ))}
    </div>
  );

  const showRowsLayout = () => (mobile ? showCardsMobile() : showCardsRow());

  const showVaults = () =>
    isLayoutsColumns ? showCardsLarge() : showRowsLayout();

  const showMoreVaultsButton = () => (
    <div className={styles.moreVaultsButton}>
      {!fetchingVaults ? (
        <Button
          buttonStyle={ButtonStyle.PrimaryRoundedFilled}
          onClick={() => getMoreVaults()}
        >
          Show more vaults
        </Button>
      ) : (
        <Spinner className={globalStyles.smallSpinner} />
      )}
    </div>
  );

  const isNotLastPage = () =>
    paginationInfo?.currentPage !== paginationInfo?.lastPage;

  const showContentView = () => (
    <>
      {showVaults()}
      <div className={styles.moreVaultsButton}>
        {vaults.length && isNotLastPage() ? showMoreVaultsButton() : ''}
      </div>
    </>
  );

  const showErrorNotification = () => (
    <Notification handleClose={() => setShowError(false)} />
  );

  return (
    <>
      {showHeaderRow()}
      <div className={globalStyles.loggedMainContainer}>
        {!fetching ? (
          <div
            className={classnames({ [styles.contentView]: isLayoutsColumns })}
          >
            {showError && showErrorNotification()}
            {showQrCodesModal && (
              <QrCodesDetailsModal
                qrCodes={qrCodesForModal}
                onClose={() => setShowQrCodesModal(false)}
              />
            )}
            {showContentView()}
          </div>
        ) : (
          <Spinner />
        )}
      </div>
    </>
  );
};

export { Vaults };
